import { useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiJson } from '@/lib/api';

export type ActivationItemId =
  | 'child'
  | 'custody'
  | 'notifications'
  | 'expense'
  | 'journal'
  | 'coparent';

export type ActivationItem = {
  id: ActivationItemId;
  completed: boolean;
};

export type ActivationResponse = {
  dismissed_at: string | null;
  completed_count: number;
  total_count: number;
  items: ActivationItem[];
};

export const ACTIVATION_QUERY_KEY = ['activation'] as const;

const EMPTY_RESPONSE: ActivationResponse = {
  dismissed_at: null,
  completed_count: 0,
  total_count: 6,
  items: [
    { id: 'child', completed: false },
    { id: 'custody', completed: false },
    { id: 'notifications', completed: false },
    { id: 'expense', completed: false },
    { id: 'journal', completed: false },
    { id: 'coparent', completed: false },
  ],
};

export function useActivationChecklist() {
  const { isLoaded, isSignedIn, getToken } = useAuth();

  return useQuery({
    queryKey: ACTIVATION_QUERY_KEY,
    queryFn: async (): Promise<ActivationResponse> => {
      try {
        return await apiJson<ActivationResponse>(getToken, '/v1/activation');
      } catch (err) {
        if (err instanceof ApiError && err.status >= 500) {
          return EMPTY_RESPONSE;
        }
        throw err;
      }
    },
    enabled: isLoaded && !!isSignedIn,
    staleTime: 30_000,
  });
}

export function useDismissActivation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<{ dismissed_at: string }> => {
      return await apiJson<{ dismissed_at: string }>(
        getToken,
        '/v1/activation/dismiss',
        { method: 'POST' }
      );
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ACTIVATION_QUERY_KEY });
      const prev = queryClient.getQueryData<ActivationResponse>(ACTIVATION_QUERY_KEY);
      if (prev) {
        queryClient.setQueryData<ActivationResponse>(ACTIVATION_QUERY_KEY, {
          ...prev,
          dismissed_at: new Date().toISOString(),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(ACTIVATION_QUERY_KEY, ctx.prev);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ACTIVATION_QUERY_KEY });
    },
  });
}

export function useActivationItemLabel() {
  return useCallback((id: ActivationItemId): string => {
    const labels: Record<ActivationItemId, string> = {
      child: 'Add your child',
      custody: 'Set up your custody schedule',
      notifications: 'Turn on notifications',
      expense: 'Log your first expense',
      journal: 'Write your first journal entry',
      coparent: 'Invite your co-parent',
    };
    return labels[id];
  }, []);
}
